import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Music2, Lock, Instagram } from "lucide-react";
import { LogoFull } from "@/components/Logo";

const LINKS = [
  { label: "Inicio", path: "/" },
  { label: "Artículos", path: "/articulos" },
  { label: "Crónicas", path: "/cronicas" },
  { label: "Plantilla", path: "/plantilla" },
  { label: "Sobre ENOLFC", path: "/sobre" },
  { label: "Contacto", path: "/contacto" },
];

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
      <div className="mx-auto flex items-center justify-between px-5 py-3.5 md:px-12">
        <Link to="/" data-testid="nav-link-inicio" onClick={() => setOpen(false)}>
          <LogoFull />
        </Link>
        <nav className="hidden items-center gap-5 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-display text-base font-bold uppercase tracking-wider transition-colors ${
                pathname === l.path ? "text-crimson" : "text-slate-300 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="https://www.tiktok.com/@enolfc"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-tiktok-button"
            className="flex items-center gap-2 rounded-full bg-white px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-ink transition-transform hover:scale-105"
          >
            <TikTokIcon /> @ENOLFC
          </a>
          <a
            href="https://www.instagram.com/enolfc_"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-button"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-slate-400 transition-colors hover:border-crimson hover:text-crimson"
            title="Instagram @ENOLFC_"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-slate-500 transition-colors hover:border-crimson hover:text-crimson"
            title="Panel de administración"
          >
            <Lock className="h-4 w-4" />
          </Link>
        </div>
        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-edge text-white lg:hidden"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  onClick={() => setOpen(false)}
                  className={`rounded-lg px-3 py-2.5 font-display text-lg font-bold uppercase tracking-wider ${
                    pathname === l.path ? "bg-crimson text-white" : "text-slate-300"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <div className="mt-2 flex items-center gap-2">
                <a
                  href="https://www.tiktok.com/@enolfc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 font-display font-bold uppercase text-ink"
                >
                  <Music2 className="h-4 w-4" /> @ENOLFC en TikTok
                </a>
                <a
                  href="https://www.instagram.com/enolfc_"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-instagram-button-mobile"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-edge text-slate-400"
                  title="Instagram @ENOLFC_"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-edge text-slate-500"
                >
                  <Lock className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
